/* eslint-disable import/no-named-as-default */
import React, { Component, Fragment, ReactNode } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Pie from "../../Charts/Pie";
import AnalyticsWidgetLoading from "../../shared/loaders/AnalyticsWidgetLoading";
import { Wrapper } from "../styles/charts-styles";
import colorPalette from "../colorPalette/colorPalette";
import { ChartTitle } from "../styles/employer-analytics-styles";
import ReactTooltip from "react-tooltip";

export interface IStageProgressionProps {
  stageProgression: {
    stages?: Array<string>;
    values?: Array<number>;
  };
  stageProgressionWidgetLoader: boolean;
}

interface IFormatterProps {
  seriesIndex: number;
  w: {
    config: {
      series: () => void;
    };
  };
  globals: {
    seriesTotals: {
      reduce: (a, b) => number;
    };
  };
}

export class StageProgression extends Component<IStageProgressionProps> {
  static propTypes = {
    stageProgression: PropTypes.shape({
      stages: PropTypes.arrayOf(PropTypes.string),
      values: PropTypes.arrayOf(PropTypes.number),
    }),
    stageProgressionWidgetLoader: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    stageProgression: {
      stages: [],
      values: [],
    },
  };

  render(): ReactNode {
    const { stageProgression, stageProgressionWidgetLoader } = this.props;
    const stageProgressionOptions = {
      series: stageProgression.values,
      options: {
        chart: {
          dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 4,
            opacity: 0.1,
          },
        },
        width: 330,
        height: 330,
        colors: colorPalette(stageProgression.stages.length),
        labels: stageProgression.stages,

        dataLabels: {
          enabled: false, // This hides data labels on pie slices
        },
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            size: "100%",
            customScale: 1,
            offsetX: 0,
            offsetY: 20,
            expandOnClick: false,
            dataLabels: {
              show: false,
              offset: 0,
              minAngleToShowLabel: 10,
            },
            donut: {
              size: "100%",
              background: "transparent",
              labels: {
                show: false,
                value: {
                  show: false,
                  fontSize: "25px",
                  offsetY: 0,
                  formatter: (val: string) => val,
                },
                total: {
                  show: false,
                  showAlways: false,
                  label: "Total",
                  fontSize: "18px",
                  fontFamily: "Komet, sans-serif",
                  color: "#333",
                  formatter: (w: IFormatterProps) => {
                    const value = w.globals.seriesTotals.reduce(
                      (a: number, b: number) => a + b,
                      0
                    );
                    return `${value}`;
                  },
                },
              },
            },
          },
        },
        tooltip: {
          enabled: true,
          style: {
            fontSize: "14px",
            fontFamily: "Komet, sans-serif",
          },
          x: {
            title: {
              formatter: (seriesName) => seriesName as unknown as string,
            },
          },
          y: {
            formatter: (value: number) => `${value}`,
          },
        },
        responsive: [
          {
            breakpoint: 1199,
            options: {
              chart: {
                width: 330,
                height: 330,
              },
              legend: {
                show: false,
              },
              dataLabels: {
                style: {
                  fontSize: "12px",
                },
              },
            },
          },
        ],
      },
    };

    return (
      <Fragment>
        <Wrapper
          className="animated fadeIn delay-5"
          id="spend-per-sector-widget"
        >
          <AnalyticsWidgetLoading
            text="Updating..."
            active={stageProgressionWidgetLoader}
          />

          <Pie
            graphOptions={stageProgressionOptions}
            dataTestId="stage-progression-chart"
          />
          <ChartTitle
            marginBottom={20}
            data-test-id="stage-progression-chart-title"
            data-tip="Number of roles at each stage of hiring process."
            data-for="stage-progression-chart-title-tooltip"
          >
            <ReactTooltip
              type="dark"
              aria-haspopup="true"
              id="stage-progression-chart-title-tooltip"
              className="tooltip-max-width"
            />
            Stage Progression
          </ChartTitle>
        </Wrapper>
      </Fragment>
    );
  }
}

interface IReduxState {
  employerAnalytics: {
    jobPerformanceData: {
      stageProgression: {
        stages?: Array<string>;
        values?: Array<number>;
      };
    };
    stageProgressionWidgetLoader: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  stageProgression: state.employerAnalytics.jobPerformanceData.stageProgression,
  stageProgressionWidgetLoader:
    state.employerAnalytics.stageProgressionWidgetLoader,
});

export default connect(mapStateToProps, {})(StageProgression);
